import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, MapPin, Calendar, Share2, X, Loader2 } from 'lucide-react';
import { getHeart, getFileUrl, getStats } from '@/lib/api';
import { cityConfig } from '@/config/cities';
import { toast } from 'sonner';

const HeartDetail = () => {
  const { number } = useParams();
  const navigate = useNavigate();
  const [heart, setHeart] = useState(null);
  const [totalDiscovered, setTotalDiscovered] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heartData, statsData] = await Promise.all([getHeart(number), getStats()]);
        setHeart(heartData);
        setTotalDiscovered(statsData.total_discovered);
      } catch (e) {
        if (e.response?.status === 404) {
          setError("Ce cœur n'a pas encore été découvert");
        } else {
          setError('Erreur lors du chargement');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [number]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${cityConfig.title} - Cœur #${heart.heart_number}`,
          text: `Cœur #${heart.heart_number} découvert à ${cityConfig.name} ! @qargo.art`,
          url: window.location.href,
        });
      } catch (e) { if (e.name !== 'AbortError') console.error(e); }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Lien copié !');
      } catch (e) { toast.error('Impossible de partager'); }
    }
  };

  if (loading) {
    return (
      <div className="fixed top-14 bottom-[60px] left-0 right-0 flex items-center justify-center z-30" style={{ background: '#000' }} data-testid="heart-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed top-14 bottom-[60px] left-0 right-0 flex flex-col items-center justify-center px-6 z-30" style={{ background: '#000' }} data-testid="heart-not-found">
        <Heart className="w-16 h-16 text-[#27272A] mb-4" />
        <h2 className="text-xl font-display font-bold mb-2 text-white">Cœur #{number}</h2>
        <p className="text-white/40 mb-6">{error}</p>
        <button onClick={() => navigate('/find')} className="text-[#E63946] text-sm font-semibold">L'as-tu trouvé ?</button>
      </div>
    );
  }

  const closePhotoUrl = getFileUrl(heart.close_photo_path);
  const discoveredDate = new Date(heart.discovered_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const discoveredTime = new Date(heart.discovered_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="fixed top-14 bottom-[60px] left-0 right-0 flex flex-col z-30"
      style={{ background: '#000', overflow: 'hidden', touchAction: 'none' }}
      data-testid="heart-detail-page"
    >
      {/* Bouton fermer — petit X en haut à droite */}
      {/* Photo moitié haute + gradient fort */}
      <div className="absolute top-0 left-0 right-0" style={{ height: '50%' }}>
        {closePhotoUrl && (
          <div className="relative w-full h-full max-w-[600px] mx-auto">
            <img src={closePhotoUrl} alt={`Cœur #${heart.heart_number}`} className="absolute inset-0 w-full h-full object-cover" />
          </div>
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 50%, #000000 100%)' }} />
      </div>

      {/* Fond noir moitié basse */}
      <div className="absolute left-0 right-0 bottom-0" style={{ height: '50%', background: '#000' }} />

      {/* X fermer */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-3 right-4 w-9 h-9 flex items-center justify-center rounded-full z-40"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}
        data-testid="back-button"
      >
        <X className="w-4.5 h-4.5 text-white" />
      </button>

      {/* Infos — centrées dans la moitié basse */}
      <div className="absolute left-0 right-0 flex flex-col items-center px-5 z-10" style={{ top: '50%', bottom: '60px', justifyContent: 'center' }}>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2 mb-1">
          <span className="font-display font-bold text-[#E63946]" style={{ fontSize: 'clamp(2.5rem, 10vw, 3.5rem)' }}>#{heart.heart_number}</span>
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-white font-bold text-xl mb-3">
          {heart.pseudo}
        </motion.p>

        {/* Ligne séparatrice rouge */}
        <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.15, duration: 0.4 }}
          className="w-12 h-[2px] bg-[#E63946] mb-4" />

        {heart.location_name && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-white text-base mb-2 text-center" style={{ textDecoration: 'none' }}>
            {heart.location_name}
          </motion.p>
        )}

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
          className="text-white/50 text-sm mb-1">
          {discoveredDate} à {discoveredTime}
        </motion.p>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="text-white/40 text-xs">
          {heart.explorer_rank}e explorateur{totalDiscovered ? ` · ${heart.explorer_rank}e cœur trouvé sur ${totalDiscovered}` : ''}
        </motion.p>

      </div>
    </div>
  );
};

export default HeartDetail;
